import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { Card as AntCard } from 'antd';
import { cn } from '@shared/lib/cn';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  bodyClassName?: string;
}

// Keep API shape (Card/CardHeader/CardTitle/...) so existing pages don't change.
// Visually we lean on antd Card for the outer chrome.

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, bodyClassName, children, ...rest }, ref) => (
    <AntCard
      ref={ref as never}
      bordered
      className={className}
      styles={{ body: { padding: 0 } }}
      {...rest}
    >
      <div className={cn('flex flex-col', bodyClassName)}>{children}</div>
    </AntCard>
  ),
);
Card.displayName = 'Card';

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-1.5 px-6 pt-5 pb-3', className)} {...props} />
  ),
);
CardHeader.displayName = 'CardHeader';

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h2 ref={ref} className={cn('m-0 text-lg font-semibold leading-tight', className)} {...props} />
  ),
);
CardTitle.displayName = 'CardTitle';

export const CardDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('m-0 text-sm opacity-70', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('px-6 pb-5 pt-0', className)} {...props} />
  ),
);
CardContent.displayName = 'CardContent';

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center gap-2 border-t border-black/5 px-6 py-3', className)}
      {...props}
    />
  ),
);
CardFooter.displayName = 'CardFooter';

export type { ReactNode };
